import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ShiftResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { MemberAvatarList, StatusPill, Switch } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAddShiftMember, useRemoveShiftMember, useShiftMembers } from '../hooks';
import { Icon } from '@/shared/icons';

/**
 * Card de turno (view "Cards"). Padrão-lista travado: card flat/borda SEM
 * checkbox (F34 — seleção em massa só na tabela) + menu ••• de ações + switch
 * inline de status (Switch shared sm — F47). Ícone do turno em slate (#64748b).
 * Preserva gestão inline de membros (add/remove).
 */
export interface ShiftCardProps {
  /** Shift data from API */
  shift: ShiftResponse;
  /** Callback when edit is clicked */
  onEdit?: (id: string) => void;
  /** Callback when delete is clicked */
  onDelete?: (id: string) => void;
  /** Callback when activate/deactivate is clicked */
  onToggleStatus?: (id: string, currentStatus: string) => void;
  /** Atalho "Operadores" — navega pro form do turno onde se gerencia membros */
  onManageMembers?: (id: string) => void;
  /** Duplica o turno (clona config; membros não são copiados) */
  onDuplicate?: (id: string) => void;
}

const WEEKDAY_ORDER: { value: number; label: string }[] = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

/** Formata weekdays[] em rótulo compacto (ex.: "Seg–Sex", "Todos os dias"). */
const formatWeekdays = (weekdays?: number[]): string => {
  if (!weekdays || weekdays.length === 0) return '';
  const set = new Set(weekdays);
  if (set.size >= 7) return 'Todos os dias';
  const selected = WEEKDAY_ORDER.filter((d) => set.has(d.value));
  if (selected.length === 0) return '';
  const positions = WEEKDAY_ORDER.map((d, i) => (set.has(d.value) ? i : -1)).filter((i) => i >= 0);
  const first = positions[0] ?? 0;
  const last = positions[positions.length - 1] ?? 0;
  const contiguous = positions.length > 1 && positions.length === last - first + 1;
  if (contiguous) {
    const a = selected[0]?.label ?? '';
    const b = selected[selected.length - 1]?.label ?? '';
    return `${a}–${b}`;
  }
  return selected.map((d) => d.label).join(', ');
};

export const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageMembers,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  // Fetch shift members
  const { data: members = [] } = useShiftMembers(shift.id);

  // Local state for instant updates
  const [localMembers, setLocalMembers] = React.useState(members);

  // Update local members when prop changes
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Fetch available users for dropdown
  const { data: allUsers = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });

  // Mutations for adding/removing users
  const addMemberMutation = useAddShiftMember();
  const removeMemberMutation = useRemoveShiftMember();

  // Map members to expected format
  const mappedMembers = localMembers.map((m) => ({
    id: m.id,
    name: m.name,
    avatar: m.avatar,
  }));

  const handleAddMember = (userId: string) => {
    // Optimistic update: add to local state immediately
    const newMember = allUsers.find((u) => u.id === userId);
    if (newMember) {
      setLocalMembers([
        ...localMembers,
        {
          id: newMember.id,
          name: newMember.name,
          email: '',
          avatar: newMember.avatar,
          shiftRole: 'primary' as const,
        },
      ]);
    }

    addMemberMutation.mutate({
      shiftId: shift.id,
      data: { userId, role: 'primary' },
    });
  };

  const handleRemoveMember = (userId: string) => {
    // Optimistic update: remove from local state immediately
    setLocalMembers(localMembers.filter((m) => m.id !== userId));

    removeMemberMutation.mutate({
      shiftId: shift.id,
      userId,
    });
  };

  // Format schedule (supports both flat and legacy nested shape)
  const sched = (shift.schedule as { fixedSchedule?: ShiftResponse['schedule'] })?.fixedSchedule ?? shift.schedule;
  const weekdaysLabel = formatWeekdays(sched?.weekdays);
  const timeLabel = sched?.startTime && sched?.endTime ? `${sched.startTime} – ${sched.endTime}` : null;
  const scheduleText = [weekdaysLabel, timeLabel].filter(Boolean).join(' · ') || 'Horário não definido';

  const isActive = shift.status === 'active';

  // Build actions for CardActionsMenu
  const actions: CardAction[] = [];
  if (onEdit) {
    actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(shift.id) });
  } else {
    actions.push({ label: 'Editar', icon: 'edit', onClick: () => navigate(`/settings/shifts/${shift.id}/edit`) });
  }
  if (onManageMembers) actions.push({ label: 'Operadores', icon: 'users', onClick: () => onManageMembers(shift.id) });
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(shift.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(shift.id, shift.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(shift.id), variant: 'danger', dividerBefore: true });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`shift-card-${shift.id}`}
    >
      {/* Header: ícone + nome/descrição + ações */}
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#64748b', color: '#ffffff' }}
        >
          <Icon name="clock" className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {shift.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {shift.description || 'Sem descrição'}
          </p>
        </div>
        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`shift-card-actions-${shift.id}`} />
          </div>
        )}
      </div>

      {/* Schedule + tipo */}
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--theme-card-text)' }}>
          <Icon name="calendar" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="truncate">{scheduleText}</span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--theme-card-text)' }}>
          <Icon name="repeat" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
          <span
            className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-medium"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
          >
            Fixo
          </span>
        </div>
      </div>

      {/* Membros (add/remove inline) */}
      <div className="mt-3">
        <div className="flex items-center gap-1 mb-2 text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
          <Icon name="users" className="w-3 h-3" />
          Membros
        </div>
        <MemberAvatarList
          members={mappedMembers}
          availableUsers={allUsers}
          limit={4}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          canRemoveMembers={false}
          loading={loadingUsers}
          placeholder="Buscar membro..."
          emptyMessage="Sem membros"
        />
      </div>

      {/* Footer: contagem de membros + status */}
      <div
        className="mt-3 pt-3 flex items-center justify-between"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {mappedMembers.length} membro{mappedMembers.length !== 1 ? 's' : ''}
        </span>
        {/* Status — F47: switch inline quando togglável; F62: StatusPill shared */}
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`shift-card-toggle-${shift.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(shift.id, shift.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            data-testid={`shift-card-status-${shift.id}`}
          />
        )}
      </div>
    </div>
  );
};

ShiftCard.displayName = 'ShiftCard';
